import {
  AbstractType,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  InjectionToken,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Type,
} from '@angular/core';
import { Router } from '@angular/router';
import { lazyInject } from '@app/containers/base/decorators';
import {
  DmDataService,
  EventsService,
  NumberFormatService,
  TransferService,
} from '@app/shared/services';
import {
  ColumnFilterModel,
  DATA_SOURCE_VALUE,
  EHeaderName,
  ENTITY_TYPES,
  FilterModel,
  FilterOptionType,
  IEvent,
  IEventStatus,
  IFieldPreferences,
} from '@app/shared/types';
import {
  TRANSFER_STATUS_TOTAL_VALUE,
  TRANSFER_STATUS_VALUE,
} from '@app/shared/types/enums/transfer-status.enum';
import { UiActions } from '@app/store/actions';
import {
  cloneDeep,
  find,
  forEach,
  get,
  head,
  includes,
  isEmpty,
  isEqual,
  isFunction,
  join,
  map,
  partition,
  set,
  size,
  split,
  unset,
} from 'lodash';
import { withDndGlobalLoader } from '../xux-dnd/decorators/xux-dnd-loader.decorators';
import { rx, Subscription } from '@app/shared/rx';
import { ITransferSummaryFilter } from '@app/shared/types/common/transfer-summary.type';
import { IProgressBarItem } from '../xux/xux-progress-bar/xux-progress-bar.component.type';
import { EVENT_SELECTOR_SELECT, TIME_FORMATS } from '@config/global-constants';
import { toMoment } from '@shared/utils';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';
import { first } from 'rxjs/operators';

interface ITransferInfo {
  count?: number;
  text?: string;
  status?: string;
  transaction_date?: string;
  isHovered?: boolean;
}

@Component({
  selector: 'transfer-summary',
  templateUrl: 'transfer-summary.component.html',
  styleUrls: ['./transfer-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransferSummaryComponent implements OnInit, OnDestroy {
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(Router) public router: Router;
  @lazyInject(TransferService) public transferService: TransferService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @lazyInject(EventsService) public eventsService: EventsService;
  @lazyInject(XVII18nService)
  public i18nService: XVII18nService;

  @Input() public field: IFieldPreferences = {};

  public defaultSummaryList: ITransferInfo[] = [];

  public summaryList: ITransferInfo[] = this.defaultSummaryList;
  public progressBarData: IProgressBarItem[] = [];

  public NoDataText: string = I18n.InfoMessages.NoTransfer;
  public transferSummaryFilter: ITransferSummaryFilter;
  public subscriptions: Subscription[] = [];
  private sourceChangeIsPos = false;

  public constructor(
    public injector: Injector,
    public cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.initializeProgressBarData();
    this.setupFormValueListeners();
  }

  public setupFormValueListeners(): void {
    const rootValueChangesSubs = this.field.control?.root.valueChanges
      .pipe(
        rx.debounceTime(100),
        rx.distinctUntilChanged(isEqual),
        rx.filter(() => {
          if (this.sourceChangeIsPos) {
            // ignore the first change when source is changed to POS

            return false;
          }

          return true;
        }),
      )
      .subscribe(() => {
        this.getTransferData();
      });

    const sourceValueChangesSubs = this.field.control?.root
      .get('source')
      ?.valueChanges.pipe(rx.distinctUntilChanged(), rx.debounceTime(50))
      .subscribe((sourceValue: DATA_SOURCE_VALUE) => {
        this.sourceChangeIsPos = false;
        if (sourceValue === DATA_SOURCE_VALUE.POS) {
          this.sourceChangeIsPos = true;
          this.handleSourceChangeToPOS();
        }
      });

    this.subscriptions.push(rootValueChangesSubs, sourceValueChangesSubs);
  }

  public async fetchEventList(): Promise<IEvent[]> {
    const params = {
      filter: `status eq in(${IEventStatus.Open},${IEventStatus.Closed})`,
      orderby: `status desc,event_days/start_date_time desc`,
      select: split(EVENT_SELECTOR_SELECT),
    };

    const eventList = (await this.eventsService
      .fetchList(params)
      .pipe(
        first(),
        rx.map((res) => get(res, 'items')),
      )
      .toPromise()) as IEvent[];

    return eventList;
  }

  public partitionEvents(eventList: IEvent[]): IEvent[] {
    const [openedEvents, closedEvents] = partition(eventList, (event) =>
      isEqual(get(event, 'status'), IEventStatus.Open),
    );
    let defaultEvents = [];
    if (!isEmpty(openedEvents)) {
      defaultEvents = openedEvents;
    } else if (!isEmpty(closedEvents)) {
      defaultEvents = [head(closedEvents)];
    }

    return defaultEvents;
  }

  public async handleSourceChangeToPOS(): Promise<void> {
    const eventList = await this.fetchEventList();
    const defaultEvents = this.partitionEvents(eventList);
    if (isEmpty(defaultEvents)) {
      this.summaryList = cloneDeep(this.defaultSummaryList);
      this.prepareProgressBarData();
      this.cdr.detectChanges();

      return;
    }
    const eventControl = this.field.control?.root.get('events');
    if (isEmpty(eventControl.value)) {
      eventControl.patchValue(map(defaultEvents, 'id'));
    }
    this.getTransferData();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public initializeProgressBarData(): void {
    this.progressBarData = [
      {
        label: this.i18nService.instant(I18n.Status.Open),
        value: 0,
        color: 'var(--widget-progress-bar-open-color)',
      },
      {
        label: this.i18nService.instant(I18n.Status.Pending),
        value: 0,
        color: 'var(--widget-progress-bar-pending-color)',
      },
      {
        label: this.i18nService.instant(I18n.Status.Completed),
        value: 0,
        color: 'var(--widget-progress-bar-completed-color)',
      },
      {
        label: this.i18nService.instant(I18n.Status.Rejected),
        value: 0,
        color: 'var(--widget-progress-bar-reject-color)',
      },
      {
        label: `${this.i18nService.instant(I18n.Status.Canceled)} & ${this.i18nService.instant(I18n.Status.Void)}`,
        value: 0,
        color: 'var(--widget-progress-bar-canceled-color)',
      },
    ];

    this.defaultSummaryList = [
      {
        status: TRANSFER_STATUS_TOTAL_VALUE.Total,
        text: this.i18nService.instant(I18n.Common.Total),
        count: 0,
      },
      {
        status: TRANSFER_STATUS_VALUE.Open,
        text: this.i18nService.instant(I18n.Status.Open),
        count: 0,
      },
      {
        status: TRANSFER_STATUS_VALUE.Pending,
        text: this.i18nService.instant(I18n.Status.Pending),
        count: 0,
      },
      {
        status: TRANSFER_STATUS_VALUE.Completed,
        text: this.i18nService.instant(I18n.Status.Completed),
        count: 0,
      },
      {
        status: TRANSFER_STATUS_VALUE.Rejected,
        text: this.i18nService.instant(I18n.Status.Rejected),
        count: 0,
      },
      {
        status: TRANSFER_STATUS_VALUE.Canceled,
        text: `${this.i18nService.instant(I18n.Status.Canceled)} & ${this.i18nService.instant(I18n.Status.Void)}`,
        count: 0,
      },
    ];
  }

  public gotoTransferPage = (
    status: TRANSFER_STATUS_VALUE | TRANSFER_STATUS_TOTAL_VALUE,
  ): void => {
    this.updateTransferListViewData(status);
    this.router.navigateByUrl(this.transferService.config.uiRoute);
  };

  private updateTransferListViewData(
    status: TRANSFER_STATUS_VALUE | TRANSFER_STATUS_TOTAL_VALUE,
  ): void {
    const type: FilterOptionType = 'containsAny';
    const filterModel: FilterModel = {};

    const {
      source,
      date,
      events,
      to_sites: siteIds,
      defaultData: { sites: allSite },
    } = this.transferSummaryFilter;

    unset(filterModel, 'transaction_date');
    unset(filterModel, 'event_id');
    unset(filterModel, 'site_id_to');

    if (size(allSite) !== size(siteIds)) {
      set(filterModel, 'site_id_to', {
        type,
        filter: siteIds,
        omitField: ['headerText'],
      });
    }

    if (DATA_SOURCE_VALUE.POS === source) {
      set(filterModel, 'event_id', {
        type,
        filter: events,
        omitField: ['headerText'],
      });
    } else {
      const dateFrom = get(date, ['value', 'dateStart']);
      const dateTo = get(date, ['value', 'dateEnd']);

      set(filterModel, 'transaction_date', {
        dateFrom,
        dateTo,
        type: 'inRangeDate',
        headerText: `${toMoment(dateFrom).format(TIME_FORMATS.L)} - ${toMoment(dateTo).format(TIME_FORMATS.L)}`,
      });
    }

    set(filterModel, 'source', this.prepareSource());
    set(filterModel, 'status', this.prepareStatus(status));
    this.uiActions.restoreDefaultListView(ENTITY_TYPES.TRANSFER);
    this.uiActions.updateListViewData({
      filterModel,
      entityType: ENTITY_TYPES.TRANSFER,
    });
  }

  public prepareStatus(
    status: TRANSFER_STATUS_VALUE | TRANSFER_STATUS_TOTAL_VALUE,
  ): ColumnFilterModel {
    const statusMap = {
      [TRANSFER_STATUS_TOTAL_VALUE.Total]: null,
      [TRANSFER_STATUS_VALUE.Canceled]: {
        type: 'containsAny',
        filter: [TRANSFER_STATUS_VALUE.Canceled, TRANSFER_STATUS_VALUE.Void],
        headerText: [
          TRANSFER_STATUS_VALUE.Canceled,
          TRANSFER_STATUS_VALUE.Void,
        ],
      },
    };

    if (
      !includes(
        [TRANSFER_STATUS_TOTAL_VALUE.Total, TRANSFER_STATUS_VALUE.Canceled],
        status,
      )
    ) {
      return {
        type: 'containsAny',
        filter: [status],
        headerText: status,
      };
    }

    return statusMap[status];
  }

  public prepareSource(): ColumnFilterModel {
    const { source } = this.transferSummaryFilter;
    const sourceFilter = {
      [DATA_SOURCE_VALUE.POS]: {
        filter: [DATA_SOURCE_VALUE.POS],
        headerText: DATA_SOURCE_VALUE.POS,
      },
      [DATA_SOURCE_VALUE.Portal]: {
        filter: [DATA_SOURCE_VALUE.Portal],
        headerText: DATA_SOURCE_VALUE.Portal,
      },
    };
    if (source === DATA_SOURCE_VALUE.All_Sources) {
      return null;
    }

    const sourceFilterData = sourceFilter[source];

    return {
      type: 'containsAny',
      ...sourceFilterData,
    };
  }

  @withDndGlobalLoader('transfers')
  public async getTransferData(): Promise<void> {
    this.transferSummaryFilter = cloneDeep(this.field.control?.root.value);
    const {
      date,
      to_sites: siteIds,
      events,
      source,
    } = this.transferSummaryFilter;

    let queryFilter: string = '';
    const sourceFilter = ` and (source ne '${DATA_SOURCE_VALUE.POS}' or (source eq '${DATA_SOURCE_VALUE.POS}' and status eq '${TRANSFER_STATUS_VALUE.Open}' and is_send eq true) or (source eq '${DATA_SOURCE_VALUE.POS}' and status ne '${TRANSFER_STATUS_VALUE.Open}'))`;
    if (source === DATA_SOURCE_VALUE.POS) {
      queryFilter = `event_id eq in(${join(events)}) and site_id_to eq in({siteGroup}) and source eq '${DATA_SOURCE_VALUE.POS}'`;
    } else {
      const dateStart = toMoment(get(date, ['value', 'dateStart']))
        .startOf('day')
        .toISOString();

      const dateEnd = toMoment(get(date, ['value', 'dateEnd']))
        .endOf('day')
        .toISOString();
      let sourceQuery = '';
      this.transferSummaryFilter.date.value.dateEnd = dateEnd;
      this.transferSummaryFilter.date.value.dateStart = dateStart;

      if (source === DATA_SOURCE_VALUE.Portal) {
        sourceQuery = ' and source eq null';
      }

      queryFilter = `transaction_date ge ${dateStart} and transaction_date lt ${dateEnd} and site_id_to eq in({siteGroup})${sourceQuery}`;
    }

    const siteGroupIds = await this.dmDataService
      .getSiteGroupId(siteIds)
      .toPromise();

    const transfers: ITransferInfo[] = await this.transferService
      .fetchList(
        {
          select: ['status'],
          filter: `${queryFilter}${sourceFilter}`,
        },
        {
          headers: {
            [EHeaderName.XSiteGroupId]: siteGroupIds,
          },
          noLoader: true,
          showErrorMessage: true,
        },
        true,
      )
      .pipe(rx.map((res) => res.items))
      .toPromise();
    this.sourceChangeIsPos = false;
    this.transformTransferData(transfers);
    this.cdr.detectChanges();
  }

  private transformTransferData(transfers: ITransferInfo[]): void {
    this.summaryList = cloneDeep(this.defaultSummaryList);
    const transferCount = size(transfers);
    forEach(transfers, (transfer) => {
      forEach(this.summaryList, (summary) => {
        if (summary.status === TRANSFER_STATUS_TOTAL_VALUE.Total) {
          summary.count = transferCount;
        }

        const isVoidOrCanceled = includes(
          [TRANSFER_STATUS_VALUE.Void, TRANSFER_STATUS_VALUE.Canceled],
          transfer.status,
        );
        if (
          isVoidOrCanceled &&
          summary.status === TRANSFER_STATUS_VALUE.Canceled
        ) {
          summary.count += 1;
        } else if (summary.status === transfer.status) {
          summary.count += 1;
        }
      });
    });

    this.prepareProgressBarData();
  }

  public prepareProgressBarData(): void {
    this.progressBarData = map(
      this.progressBarData,
      (progressBarItem: IProgressBarItem) => {
        const target = find(
          this.summaryList,
          (item) => progressBarItem.label === item.text,
        );

        return {
          ...progressBarItem,
          value: get(target, 'count', 0),
        };
      },
    );
  }

  public handleHover(hoveredData: IProgressBarItem): void {
    forEach(this.summaryList, (item: ITransferInfo) => {
      item.isHovered = hoveredData?.label === item.text ? true : false;
    });
  }
}
